/**
 * Parser de streaming structurel — platform-agnostique.
 *
 * Ingère le flux de tokens des endpoints `research-only` / `generation-only` et émet des
 * `StructuredBlock`. Reproduit le contrat de l'add-in Word (`apps/addin-word/src/services/insertion/`)
 * SANS le coupler à Office.js, pour permettre la convergence avec le canvas Plate web.
 *
 * Référence du contrat : `docs/features/act-generation/plans/2026-05-27-redaction-web-canvas.html` §07c.
 *
 * Invariants critiques (cf. plan §07c.7) :
 *  1. Flush au délimiteur COMPLET (`[/SECTION]` / `[/TITRE]` / `[/PARA]`, + variantes malformées `>`).
 *  2. NE JAMAIS flusher un délimiteur partiel en fin de buffer (`[/PA…`) : attendre la suite.
 *  3. Skip-until-first-`#` : jeter tout ce qui précède le premier `#` (préambule LLM, <think>).
 *  4. Buffer borné : flush au délimiteur → on ne re-parse pas tout (perf).
 *  5. Finalize best-effort à la fin du stream.
 *
 * Périmètre actuel : délimiteurs, `[Hector-XX]`, headings markdown, marqueurs juridiques,
 * gras `**`, guillemets typographiques → italique.
 *
 * Source unique des marqueurs (#1001) : le champ `test` de `MARKER_RULES` n'embarque plus une
 * seule regex — il appelle les fonctions `est…Ancre` de `./marqueurs.js`, où vit désormais TOUT
 * le vocabulaire des marqueurs, dans ses deux lectures : celle de l'add-in (à préfixe) et celle
 * du canvas (ancrée, marqueur seul sur sa ligne). Les deux sont côte à côte dans le même
 * fichier, ce que la duplication add-in ↔ web ne permettait pas.
 *
 * ⚠️ NE PAS remplacer une variante ancrée par le prédicat add-in de même nom : le canvas a
 * besoin de l'ancrage, et pas seulement par prudence. Ses règles portent `pageBreakBefore`, et
 * surtout le mode compact de l'en-tête de courrier repose sur le NON-match — « OBJET :
 * recouvrement de la créance … » ne doit matcher AUCUNE règle pour que `turnsOnReducedSpacing`
 * s'allume. La substitution a été tentée le 2026-08-16 et a cassé quatre gardes live ;
 * `marqueurs-reference-web.test.ts` verrouille désormais le comportement attendu.
 *
 * `MARKER_RULES` garde sa forme propre au canvas (`styleKey`, `leadingBreaks`,
 * `pageBreakBefore`…) : l'APPLICATION de la mise en forme reste spécifique à chaque hôte.
 */

import {
  estBordereauAncre,
  estContreOuDevantAncre,
  estDevantJugeAncre,
  estEnTeteBordereauAncre,
  estEnvoiRecommandeAncre,
  estInformationsRelativesAncre,
  estLabelDePartieAncre,
  estMarqueurDeBlocAncre,
  estParCesMotifsAncre,
  estPieceAncre,
  estPiecesJointesAncre,
  estPlaiseAncre,
  estPourAncre,
  estPourPersonneAncre,
  estSalutationAncre,
  estTresImportantAncre,
  isInventaireHeadingLine,
  isInventairePieceLine,
} from './marqueurs.js';
import { endsAdministrativeInventory } from '../documents/pieceNamingConvention.js';
import { estUrlWebSure } from '../webUrl.js';
import type {
  HectorStyleKey,
  InlineRun,
  ListBlock,
  ListItem,
  ParagraphBlock,
  StructuredBlock,
  TableBlock,
  TableCell,
  TableCellParagraph,
  TableRow,
} from './index.js';

// --- Délimiteurs structurels (matchent la variante malformée `>` comme l'add-in) ---
const DELIM = /\[\/(?:SECTION|TITRE|PARA(?::(NOUVEAU|ENRICHI))?)[\]>]/i;
// Catch-all : tout [/XXX] inconnu (hors [/Hector-XX]) → traité comme fin de para.
const UNKNOWN_DELIM = /\[\/(?!Hector-)[A-Z]+[\]>]/gi;
// Tag de style nommé pleine-ligne : [Hector-XX]contenu[/Hector-XX]
const HECTOR_PARAGRAPH = /^\s*\[Hector-([\w-]+)\]([\s\S]*?)\[\/Hector-\1\]\s*$/;
const STRIP_HECTOR = /\[\/?Hector-[\w-]+\]/g;
const THINK = /<think>[\s\S]*?<\/think>|<thinking>[\s\S]*?<\/thinking>/gi;

export interface ParserCallbacks {
  /** Appelé pour chaque bloc prêt. Append-only (ordre garanti). */
  onBlock: (block: StructuredBlock) => void;
}

export interface ParserOptions {
  /**
   * Que faire du texte qui précède le premier marqueur de structure (`#` ou
   * `[Hector-`).
   *
   * - `'ignorer'` (défaut) : c'est un FLUX de génération d'acte. Le modèle y
   *   écrit un préambule avant l'acte (prose, `<think>`, balise de code), et ce
   *   préambule ne doit pas entrer dans le document (invariant 3).
   * - `'conserver'` : c'est un DOCUMENT ENTIER, sans préambule. Tout est
   *   contenu dès le premier caractère.
   *
   * ⚠️ Un document entier passé en `'ignorer'` est amputé, pas refusé : un
   * courrier commence par l'expéditeur, pas par un titre. Sans aucun `#`, il
   * n'en restait que les 64 derniers caractères, émis par `finalize()` comme un
   * document valide (`write_markdown`, 85 conversations de production du
   * 2026-09-25 au 2026-10-09).
   */
  preambule?: 'ignorer' | 'conserver';
}

interface MarkerRule {
  test: (line: string) => boolean;
  styleKey: HectorStyleKey;
  pageBreakBefore?: boolean;
  leadingBreaks?: number;
  trailingBreaks?: number;
}

/**
 * Marqueurs juridiques — table de règles ordonnée (premier match gagne).
 * Port direct du pipeline add-in (`wordInserter.ts` §5/6 leading/trailing,
 * `formattingRules.ts` isXxxContent), avec :
 *  - `pageBreakBefore` : saut de page avant le bloc (PLAISE, BORDEREAU,
 *    PIÈCES JOINTES — cf. add-in §4).
 *  - `leadingBreaks` : N paragraphes vides AVANT (POUR=3, autres=1 — §5).
 *  - `trailingBreaks` : N paragraphes vides APRÈS (§6 — quasi tous les
 *    marqueurs d'en-tête en ont 1, sauf PLAISE et section romaine).
 *
 * Les regexp acceptent toutes l'enrobage `**...**` markdown (le LLM ouvre
 * souvent les marqueurs en gras). Les majuscules sont gérées par le flag `i`.
 */

const MARKER_RULES: MarkerRule[] = [
  // ─── Page break + style spécial ────────────────────────────────────────
  // PLAISE — le premier reçoit pageBreakBefore + style centré gras ; les
  // suivants sont re-mappés en Hector-PLAISE-Body via `seenPLAISE`. Pas de
  // trailingBreaks (add-in §6, exclusion explicite `&& !isPlaiseContent`).
  { test: estPlaiseAncre, styleKey: 'Hector-PLAISE', pageBreakBefore: true },
  // INFORMATIONS RELATIVES À LA RELATION DE TRAVAIL — marqueur bold dédié
  // (actes prud'homaux). leadingBreaks=2 / trailingBreaks=1 cf. add-in §6.
  {
    test: estInformationsRelativesAncre,
    styleKey: 'Hector-Marqueur-Bold',
    leadingBreaks: 2,
    trailingBreaks: 1,
  },
  // POUR MONSIEUR / POUR MADAME <Nom> — variante avec nom du destinataire :
  // bold + underline. Distinct du « POUR : » nu, qui sert d'intitulé de partie.
  {
    test: estPourPersonneAncre,
    styleKey: 'Hector-Marqueur-BU',
    leadingBreaks: 1,
    trailingBreaks: 1,
  },
  // BORDEREAU / LISTE DES PIÈCES COMMUNIQUÉES — ligne entièrement en capitales
  // (la garde qui empêche une phrase de corps de déclencher un saut de page).
  // BORDEREAU garde l'aération que Word ajoute avant la première pièce. À la
  // différence de PIÈCES JOINTES ci-dessous, cette ligne reçoit donc aussi le
  // paragraphe vide terminal porté par `trailingBreaks`.
  {
    test: estBordereauAncre,
    styleKey: 'Hector-BORDEREAU',
    pageBreakBefore: true,
    trailingBreaks: 1,
  },
  // PIÈCES JOINTES : pas de trailingBreaks. L'add-in insère le saut de page
  // puis le bloc, sans paragraphe vide après ; l'aération vient du
  // `spacingAfter_pt` du style. Un trailing en plus créait un saut parasite
  // avant le premier item.
  { test: estPiecesJointesAncre, styleKey: 'Hector-Pieces-Jointes', pageBreakBefore: true },

  // ─── Marqueurs sans page break ─────────────────────────────────────────
  { test: estParCesMotifsAncre, styleKey: 'Hector-PAR-CES-MOTIFS' },
  { test: estTresImportantAncre, styleKey: 'Hector-TRES-IMPORTANT', leadingBreaks: 1, trailingBreaks: 1 },

  // POUR / CONTRE / DEVANT — Hector-Marqueur-BU (bold + underline). POUR
  // reçoit 3 leadingBreaks (add-in `wordInserter.ts:1696-1700`).
  { test: estPourAncre, styleKey: 'Hector-Marqueur-BU', leadingBreaks: 3, trailingBreaks: 1 },
  { test: estContreOuDevantAncre, styleKey: 'Hector-Marqueur-BU', leadingBreaks: 1, trailingBreaks: 1 },
  // DEVANT MONSIEUR/MADAME LE JUGE … — variante avec juridiction.
  { test: estDevantJugeAncre, styleKey: 'Hector-Marqueur-BU', leadingBreaks: 1, trailingBreaks: 1 },

  // ─── Labels de parties — aligné à droite, gras italique ────────────────
  // leadingBreaks=1 : l'add-in insère un paragraphe vide AVANT le label,
  // sans quoi il colle visuellement au paragraphe précédent.
  { test: estLabelDePartieAncre, styleKey: 'Hector-Party-Label', leadingBreaks: 1, trailingBreaks: 1 },

  // ─── Salutation « Madame, Monsieur, » ──────────────────────────────────
  // Placée AVANT l'alternance Marqueur-Bold (premier match gagne).
  { test: estSalutationAncre, styleKey: 'Hector-Marqueur-Bold', leadingBreaks: 1, trailingBreaks: 1 },

  // ─── Marqueurs Hector-Marqueur-Bold (gras, aligné à gauche) ────────────
  { test: estMarqueurDeBlocAncre, styleKey: 'Hector-Marqueur-Bold', leadingBreaks: 1, trailingBreaks: 1 },

  // ─── Pièces ─────────────────────────────────────────────────────────────
  { test: estPieceAncre, styleKey: 'Hector-Piece-Before' },
];

/**
 * « Par lettre recommandée … » — traité HORS `MARKER_RULES` (#1001).
 *
 * POURQUOI pas une règle de style : Word pose italique + souligné en DIRECT sur
 * le run (`wordInserter.ts:2172-2181`, `isParLettreContent`), et aucun des 32
 * styles `Hector-*` ne combine italique ET souligné (`styleSpec.ts`). Le bloc
 * sort donc en `Hector-Normal` avec les marques portées par ses runs (D6).
 *
 * POURQUOI desserré : la règle web ancrait en fin de ligne, donc le suffixe
 * usuel « avec avis de réception » faisait échouer la détection. L'add-in, lui,
 * matche toute ligne qui COMMENCE par la formule
 * (`formattingRules.ts:841 isParLettreRecommandeeBlock`).
 */
const estEnvoiRecommande = estEnvoiRecommandeAncre;

const HEADING_STYLE: Record<number, HectorStyleKey> = {
  1: 'Hector-H1', 2: 'Hector-H2', 3: 'Hector-H3', 4: 'Hector-H4', 5: 'Hector-H5', 6: 'Hector-H6',
};

/**
 * Quotes ouverts → fermants. Pour les guillemets symétriques (`"`), le
 * fermant est identique. Les apostrophes typographiques U+2018/U+2019
 * sont volontairement absentes (= apostrophes françaises, pas guillemets).
 */
const QUOTE_CLOSE: Record<string, string> = {
  '«': '»',
  '“': '”', // “ → ”
  '"': '"',           // symétrique
};

/**
 * Détecte si une ligne de heading correspond à un type de document "Mise en
 * demeure-like" — supprime le page break avant la 1ère section romaine
 * (port direct `MiseEnDemeureStateManager` add-in §8).
 */
function isMiseEnDemeureDocTitle(headingText: string): boolean {
  return /(mise en demeure|renvoi d'audience|ajournement|mise en libert[éE]|requ[êÊ]te EN)/i.test(
    headingText,
  );
}

/**
 * Détecte si une ligne (heading ou body) introduit une section romaine
 * `I.`, `I)`, `I-`, `I]`. Retourne le suffixe pour pouvoir matcher EXPOSÉ DES
 * FAITS, PRÉAMBULE, etc. en suivant.
 */
function startsWithRomanI(line: string): boolean {
  return /^I[.\-)\]]\s*/i.test(line);
}

/**
 * Marqueurs qui ACTIVENT le mode reduced-spacing (paragraphes suivants en
 * Hector-Normal-Compact). Issu de `wordInserter.ts:1614-1625`.
 */
function turnsOnReducedSpacing(line: string): boolean {
  return /^(\*\*)?(POUR|CONTRE|DEVANT|OBJET|[AÀ] LA REQU[EÊ]TE DE|J'AI DONN[EÉ] (ASSIGNATION|COMMANDEMENT)|J'AI HUISSIER|J'AI COMMISSAIRE|ENTRE LES SOUSSIGN[EÉ]S?|ENTRE|ET CONTRE)\s*:?(\*\*)?/i.test(
    line,
  );
}

/**
 * Marqueurs qui DÉSACTIVENT le mode reduced-spacing. Issu de
 * `wordInserter.ts:1582-1585`.
 */
function turnsOffReducedSpacing(line: string): boolean {
  return /^(\*\*)?(TR[EÈ]S\s+IMPORTANT|PLAISE|MADAME|MONSIEUR|EXPOS[EÉ] DES FAITS|RAPPEL DES FAITS|PR[EÉ]AMBULE|TOTAL D[UÛ]|Objet de la demande|L'AN DEUX MILLE)/i.test(
    line,
  );
}

/**
 * Une ligne de table Markdown : `| col1 | col2 |` (au moins 1 séparateur
 * intérieur et bornée de pipes). Rejette les séparateurs `|---|---|`.
 */
function isTableRow(line: string): boolean {
  return /^\|[^\n]+\|$/.test(line) && !isTableSeparator(line);
}

/**
 * Le séparateur header/body d'une table Markdown : `|---|---|`, `| :---: |`,
 * `|-|-|`. Composé exclusivement de `|`, `-`, `:`, espaces.
 */
function isTableSeparator(line: string): boolean {
  return /^\|[\s\-:|]+\|$/.test(line) && line.includes('-');
}

/**
 * Lit l'alignement de chaque colonne sur la ligne de séparateur GFM.
 * `:---` gauche, `:---:` centré, `---:` droite, `---` non spécifié (`null`).
 */
function parseColumnAlignments(separator: string): (('left' | 'center' | 'right') | null)[] {
  return separator
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => {
      const c = cell.trim();
      const left = c.startsWith(':');
      const right = c.endsWith(':');
      if (left && right) return 'center' as const;
      if (left) return 'left' as const;
      if (right) return 'right' as const;
      return null;
    });
}

/** Un item de liste reconnu sur une ligne : sa nature, son texte, son éventuel numéro de départ. */
interface ListItemMatch {
  ordered: boolean;
  text: string;
  ordinal?: number;
}

/**
 * Reconnaît un item de liste GFM — `- `, `* `, `+ ` (puces) ou `1. ` / `1) `
 * (numérotées).
 *
 * ⚠️ **Deux formes qui ressemblent à une puce et n'en sont pas**, écartées
 * explicitement parce qu'elles existent déjà dans le corpus d'actes :
 *  - la règle horizontale `---` / `***` / `___`, qui est du bruit markdown ;
 *  - le séparateur typographique `* * *`, qui est un vrai bloc centré.
 * Sans ces gardes, `* * *` deviendrait un item de liste au texte `* *`.
 */
function matchListItem(line: string): ListItemMatch | null {
  if (/^[-_*]{3,}$/.test(line)) return null;
  if (/^(\*\s*){2,5}$/.test(line)) return null;
  // `•` n'est pas du markdown : c'est la puce que le prompt de rendu impose
  // aujourd'hui aux jobs d'actes, **précisément parce que** le parseur ne
  // savait pas lire les listes. La reconnaître ici est ce qui fait sortir les
  // actes existants en vraies listes sans toucher au prompt — et ce qui rend
  // le changement démontrable sur le corpus réel plutôt que sur un cas neuf.
  const bullet = /^[-*+•]\s+(.+)$/.exec(line);
  if (bullet && bullet[1] !== undefined) return { ordered: false, text: bullet[1].trim() };
  const ordered = /^(\d{1,9})[.)]\s+(.+)$/.exec(line);
  if (ordered && ordered[1] !== undefined && ordered[2] !== undefined) {
    return { ordered: true, text: ordered[2].trim(), ordinal: Number(ordered[1]) };
  }
  return null;
}

/** Profondeur d'imbrication d'un item, déduite de son indentation source (2 espaces = 1 niveau). */
const INDENT_PER_LEVEL = 2;
const MAX_LIST_LEVEL = 8;

/**
 * Au-delà, une ligne capturée dans le bloc « expéditeur / destinataire » est
 * de la prose, pas une ligne d'adresse. Valeur de jugement : une ligne
 * d'adresse postale ou de qualité d'une partie tient largement en dessous.
 */
const LONGUEUR_MAX_DUNE_LIGNE_DADRESSE = 160;

export class StructuralParser {
  private buffer = '';
  /** Métadonnée du seul paragraphe en cours d'émission, jamais du suivant. */
  private changeBarEnCours: ParagraphBlock['changeBar'];
  private sawFirstHeading: boolean;

  // ─── State managers (scope = instance, reset implicite au new) ────────────
  //
  // Port direct des state managers add-in `stateManagers.ts`. Vivent dans
  // la classe (PAS en singletons globaux) → un nouveau StructuralParser =
  // état neuf, pas de fuite entre 2 sessions de rédaction.

  /** 1er PLAISE = pageBreak + style centered ; suivants = PLAISE-Body justified. */
  private seenPLAISE = false;
  /** 1ère section romaine `I.` = pageBreak (sauf si PLAISE déjà vu OU MEnDemeure). */
  private seenRomanSection = false;
  /** Type de document qui supprime le page break avant I. (mise en demeure et apparentés). */
  private isMiseEnDemeureDoc = false;
  /** Reduced spacing actif → paragraphes "plain" en Hector-Normal-Compact. */
  private reducedSpacingActive = false;
  /**
   * Bordereau entré → les Pièces n°X qui suivent passent du style
   * `Hector-Piece-Before` (bold, droite) au style `Hector-Piece-After`
   * (normal, gauche). Port direct `BordereauStateManager` add-in.
   * Activé dès qu'un bloc `Hector-BORDEREAU`, `Hector-Pieces-Jointes` ou
   * `LISTE DES PIÈCES` apparaît dans le stream.
   */
  private bordereauActive = false;
  /**
   * Titre `INVENTAIRE DES PIÈCES` vu (droit public, #1704) → une ligne à numéro nu
   * (« 1 Carte d'identité ») est une ligne de pièce, stylée comme « Pièce n°1 : … » sous un
   * bordereau (`Hector-Piece-After`). Jamais ouvert par un titre BORDEREAU : le rendu civil
   * ne lit pas les numéros nus.
   */
  private inventaireActive = false;
  /** Quote ouvert non fermé → le prochain bloc commence italique jusqu'au fermant. */
  private crossQuote: { isInside: boolean; openChar: string | null } = {
    isInside: false,
    openChar: null,
  };

  /**
   * Accumulateur tables Markdown (lignes `| col1 | col2 |` consécutives).
   * Le séparateur `|---|---|` marque la 1ère row comme header. Flushé dès
   * qu'on rencontre une ligne hors-table.
   */
  private pendingTable: {
    rows: TableRow[];
    headerFirstRow: boolean;
    columnAlignments?: (('left' | 'center' | 'right') | null)[];
  } | null = null;

  /**
   * Accumulateur de liste GFM — même raison d'être que `pendingTable`, et une
   * raison de plus qui lui est propre : le canvas écrit par deltas, donc une
   * liste émise item par item sortirait en plusieurs listes Word et une
   * numérotation ordonnée **recommencerait à 1** à chaque reprise du flux.
   * Flushé dès qu'une ligne hors liste arrive, ou que la nature de la liste
   * change (puces → numérotée).
   */
  private pendingList: {
    ordered: boolean;
    start?: number;
    items: ListItem[];
  } | null = null;

  /**
   * Capture mise en demeure 2 colonnes. Démarre sur EXPÉDITEUR, switch
   * colonne droite sur DESTINATAIRE, flush sur "Par lettre recommandée" /
   * OBJET / fin de stream. Port `MiseEnDemeureStateManager` add-in §8.
   */
  private mDemeureCapture: {
    left: TableCellParagraph[];
    right: TableCellParagraph[];
    side: 'left' | 'right';
  } | null = null;

  constructor(private readonly cb: ParserCallbacks, options: ParserOptions = {}) {
    this.sawFirstHeading = options.preambule === 'conserver';
  }

  /** Pousse un token/chunk SSE. Émet les blocs prêts via `onBlock`. */
  push(chunk: string): void {
    this.buffer += chunk;
    // Invariant 3 : skip-until-first-marker. Jeter le préambule (prose LLM, <think>,
    // ``` ouvert) jusqu'au premier marqueur de structure. Deux marqueurs acceptés :
    //  - `#` (mode markdown headings, utilisé par les endpoints research-only/generation-only)
    //  - `[Hector-` (mode tag pleine-ligne, utilisé par le canvas web — l'agent reçoit
    //    une instruction de répondre EXCLUSIVEMENT en [Hector-XX], il n'émet pas de `#`).
    if (!this.sawFirstHeading) {
      const hash = this.buffer.indexOf('#');
      const hector = this.buffer.indexOf('[Hector-');
      // Premier marqueur trouvé (le plus à gauche des deux, en ignorant -1).
      let firstMarker = -1;
      if (hash !== -1 && hector !== -1) firstMarker = Math.min(hash, hector);
      else if (hash !== -1) firstMarker = hash;
      else if (hector !== -1) firstMarker = hector;

      if (firstMarker === -1) {
        // Pas encore de marqueur : garder une petite fenêtre (un marqueur peut arriver
        // collé sur plusieurs chunks — `[Hector-` fait 8 chars, on garde 64 pour marge).
        if (this.buffer.length > 64) this.buffer = this.buffer.slice(-64);
        return;
      }
      this.buffer = this.buffer.slice(firstMarker);
      this.sawFirstHeading = true;
    }
    this.drain(false);
  }

  /** Fin du stream : vide ce qui reste (best-effort, invariant 5). */
  finalize(): void {
    this.drain(true);
    const tail = this.stripDelims(this.buffer).trim();
    if (tail) this.emitLine(tail);
    // Flushe les accumulators en attente (liste, table Markdown, mise-en-demeure
    // capture) pour ne pas perdre du contenu en fin de stream.
    this.flushPendingList();
    this.flushPendingTable();
    this.flushMDemeureCapture();
    this.buffer = '';
  }

  // --- interne ---

  private drain(flushAll: boolean): void {
    // Invariant 1 : tant qu'un délimiteur COMPLET est présent, extraire le segment qui précède.
    for (;;) {
      const m = DELIM.exec(this.buffer);
      if (!m) break;
      const segment = this.buffer.slice(0, m.index);
      this.buffer = this.buffer.slice(m.index + m[0].length);
      const line = this.stripDelims(segment).trim();
      const suffixe = m[1]?.toLowerCase();
      const changeBar = suffixe === 'nouveau' || suffixe === 'enrichi' ? suffixe : undefined;
      if (line) this.emitLine(line, changeBar);
    }
    if (flushAll) return;
    // Invariant 2 : si le buffer se termine par un délimiteur PARTIEL (`...[/PA`), ne rien faire,
    // attendre la suite. Sinon on risquerait de couper un délimiteur en deux.
    const partial = /\[\/?[A-Za-z-]*$/.test(this.buffer);
    if (partial) return;
    // Pas de délimiteur en attente : on conserve le buffer (flushé au prochain délim).
  }

  private stripDelims(s: string): string {
    return s.replace(DELIM, '\n').replace(UNKNOWN_DELIM, '\n').replace(THINK, '');
  }

  private emitLine(raw: string, changeBar?: ParagraphBlock['changeBar']): void {
    // Un segment peut contenir plusieurs lignes (ex: bloc multi-\n). Émettre une par une.
    const lignes = raw.split('\n');
    let dernierContenu = -1;
    for (let index = lignes.length - 1; index >= 0; index -= 1) {
      if (lignes[index]?.trim()) {
        dernierContenu = index;
        break;
      }
    }
    for (const [index, line] of lignes.entries()) {
      const t = line.trim();
      if (!t) continue;
      if (/^<\/?step[_\d]*.*>$/i.test(t)) continue; // workflow tag → skip

      // Le suffixe vit sur le délimiteur de FIN : dans un segment multi-ligne,
      // seul le dernier paragraphe reçoit donc la barre. La valeur précédente
      // est restaurée pour garder les appels récursifs synchrones sans fuite.
      const changeBarPrecedente = this.changeBarEnCours;
      this.changeBarEnCours = index === dernierContenu ? changeBar : undefined;
      try {
        // ── Listes GFM — accumulation cross-ligne ──────────────────────────
        // AVANT les tables et la capture mise en demeure : une liste ouverte
        // doit se fermer proprement quel que soit le bloc qui la suit, et un
        // item ne doit jamais tomber dans la capture 2 colonnes.
        const item = this.mDemeureCapture ? null : matchListItem(t);
        if (item) {
          this.flushPendingTable();
          // Changer de nature ferme la liste courante : `- puce` puis `1. num`
          // sont deux listes, pas une liste hybride.
          if (this.pendingList && this.pendingList.ordered !== item.ordered) this.flushPendingList();
          if (!this.pendingList) {
            this.pendingList = {
              ordered: item.ordered,
              // `start` n'a de sens que sur une liste numérotée qui ne commence
              // pas à 1 — sinon on écrirait un `w:start` redondant dans Word.
              start: item.ordered && item.ordinal !== undefined && item.ordinal !== 1 ? item.ordinal : undefined,
              items: [],
            };
          }
          const indent = line.length - line.trimStart().length;
          this.pendingList.items.push({
            runs: this.parseInlineWithState(item.text),
            level: Math.min(Math.floor(indent / INDENT_PER_LEVEL), MAX_LIST_LEVEL),
            ...(this.changeBarEnCours === undefined ? {} : { changeBar: this.changeBarEnCours }),
          });
          continue;
        }
        this.flushPendingList();

        // ── Tables Markdown — accumulation cross-ligne ─────────────────────
        if (isTableSeparator(t)) {
          // Ligne `|---|---|` → la row précédente devient header, et la ligne
          // porte l'alignement de chaque colonne.
          if (this.pendingTable) {
            this.pendingTable.headerFirstRow = true;
            this.pendingTable.columnAlignments = parseColumnAlignments(t);
          }
          continue;
        }
        if (isTableRow(t)) {
          if (!this.pendingTable) {
            this.pendingTable = { rows: [], headerFirstRow: false };
          }
          this.pendingTable.rows.push(this.parseTableRow(t));
          continue;
        }
        // Non-table : flush la table en cours AVANT d'émettre la ligne courante.
        this.flushPendingTable();

        // ── Mise en demeure capture — accumulation 2 colonnes ──────────────
        // Sur EXPÉDITEUR : démarre la capture.
        if (/^(\*\*)?EXP[EÉ]DITEUR\b/i.test(t) && !this.mDemeureCapture) {
          this.mDemeureCapture = { left: [], right: [], side: 'left' };
          this.mDemeureCapture.left.push({
            runs: this.parseInlineWithState(t.replace(/\*\*/g, '')),
            styleKey: 'Hector-Marqueur-Bold',
          });
          continue;
        }
        // En capture : DESTINATAIRE switch sur la droite.
        if (this.mDemeureCapture && /^(\*\*)?DESTINATAIRE\b/i.test(t)) {
          this.mDemeureCapture.side = 'right';
          this.mDemeureCapture.right.push({
            runs: this.parseInlineWithState(t.replace(/\*\*/g, '')),
            styleKey: 'Hector-Marqueur-Bold',
          });
          continue;
        }
        // En capture : la première ligne qui n'est plus une ligne d'adresse
        // ferme la capture, puis est émise normalement. Sans les trois derniers
        // cas, un acte sans « OBJET » voyait ses titres et tout son corps
        // empilés dans la colonne droite, `##` en clair, sous un reçu « ok »
        // (constaté le 2026-10-05 sur une mise en demeure écrite par l'agent).
        if (
          this.mDemeureCapture &&
          (/^(\*\*)?(PAR LETTRE RECOMMAND[EÉ]E|OBJET)\b/i.test(t) ||
            /^#{1,6}\s/.test(t) ||
            t.startsWith('[Hector-') ||
            t.length > LONGUEUR_MAX_DUNE_LIGNE_DADRESSE)
        ) {
          this.flushMDemeureCapture();
          this.emitBlock(t);
          continue;
        }
        // En capture : accumuler la ligne dans la colonne courante.
        if (this.mDemeureCapture) {
          const side = this.mDemeureCapture.side;
          this.mDemeureCapture[side].push({
            runs: this.parseInlineWithState(t.replace(/\*\*/g, '')),
          });
          // Filet de sécurité : > 30 lignes capturées sans trigger de fin →
          // flush forcé (le doc n'est probablement pas une vraie mise en demeure).
          if (this.mDemeureCapture[side].length > 30) this.flushMDemeureCapture();
          continue;
        }

        this.emitBlock(t);
      } finally {
        this.changeBarEnCours = changeBarPrecedente;
      }
    }
  }

  /** Émet un paragraphe en y joignant la métadonnée du délimiteur courant. */
  private emitParagraph(block: Omit<ParagraphBlock, 'changeBar'>): void {
    this.cb.onBlock({
      ...block,
      ...(this.changeBarEnCours === undefined ? {} : { changeBar: this.changeBarEnCours }),
    });
  }

  /**
   * Parse une ligne `| col1 | col2 | col3 |` en TableRow. Strip les pipes
   * extérieurs puis split sur `|`. Chaque cellule = 1 paragraphe (Markdown
   * tables ne supportent pas le multi-paragraphe natif).
   */
  private parseTableRow(line: string): TableRow {
    const inner = line.replace(/^\|/, '').replace(/\|$/, '');
    const cells: TableCell[] = inner.split('|').map((c) => ({
      paragraphs: [{ runs: this.parseInlineWithState(c.trim()) }],
    }));
    return {
      cells,
      ...(this.changeBarEnCours === undefined ? {} : { changeBar: this.changeBarEnCours }),
    };
  }

  private flushPendingTable(): void {
    if (!this.pendingTable || this.pendingTable.rows.length === 0) {
      this.pendingTable = null;
      return;
    }
    const table: TableBlock = {
      kind: 'table',
      rows: this.pendingTable.rows,
      headerFirstRow: this.pendingTable.headerFirstRow,
      borders: 'all',
      ...(this.pendingTable.columnAlignments
        ? { columnAlignments: this.pendingTable.columnAlignments }
        : {}),
    };
    this.pendingTable = null;
    this.cb.onBlock(table);
  }

  private flushPendingList(): void {
    if (!this.pendingList || this.pendingList.items.length === 0) {
      this.pendingList = null;
      return;
    }
    const block: ListBlock = {
      kind: 'list',
      ordered: this.pendingList.ordered,
      items: this.pendingList.items,
      ...(this.pendingList.start !== undefined ? { start: this.pendingList.start } : {}),
    };
    this.pendingList = null;
    this.cb.onBlock(block);
  }

  private flushMDemeureCapture(): void {
    if (!this.mDemeureCapture) return;
    const left = this.mDemeureCapture.left;
    const right = this.mDemeureCapture.right;
    this.mDemeureCapture = null;
    // Une seule colonne (pas de DESTINATAIRE détecté) → flusher en paragraphes
    // normaux pour ne pas perdre le contenu.
    if (right.length === 0) {
      for (const p of left) {
        this.emitParagraph({
          kind: 'paragraph',
          styleKey: p.styleKey ?? 'Hector-Normal',
          runs: p.runs,
        });
      }
      return;
    }
    // Table 1×2 sans bordures.
    const table: TableBlock = {
      kind: 'table',
      rows: [
        {
          cells: [{ paragraphs: left }, { paragraphs: right }],
        },
      ],
      headerFirstRow: false,
      borders: 'none',
    };
    this.cb.onBlock(table);
  }

  private emitBlock(line: string): void {
    // ─── 0) Bascules d'espacement — ÉVALUÉES À CHAQUE LIGNE (#1001, D4) ───
    // AVANT : les deux bascules n'étaient testées qu'à l'intérieur d'une règle
    // de MARKER_RULES déjà matchée. Un « OBJET : MISE EN DEMEURE » (suffixe →
    // aucune règle ne matche) n'allumait donc jamais le mode compact, et
    // symétriquement une salutation non détectée ne l'éteignait plus : tout le
    // corps sortait en `Hector-Normal-Compact` (spacingAfter=0). L'add-in
    // évalue ces bascules sur la ligne courante, indépendamment de toute règle
    // (`wordInserter.ts:1723-1727` : `shouldTurnOffSpacing` calculé sur
    // `cleanLine`, hors de la chaîne de styles). On fait pareil, ici, en tête
    // d'emitBlock : la bascule vaut donc pour TOUS les chemins d'émission
    // (tag [Hector-XX], heading, marqueur, hyperlien, paragraphe plain).
    // OFF est évalué après ON : une ligne qui déclencherait les deux éteint.
    const cleaned = line.replace(STRIP_HECTOR, '');
    if (turnsOnReducedSpacing(cleaned)) this.reducedSpacingActive = true;
    if (turnsOffReducedSpacing(cleaned)) this.reducedSpacingActive = false;
    // L'inventaire administratif se ferme au premier intitulé qui n'est pas une
    // pièce (titre, annexe en gras ou en capitales) : sans cette borne, les lignes
    // numérotées d'une annexe de jurisprudences qui suit l'inventaire seraient
    // stylées comme des pièces. Même borne que l'insertion Word (#1704).
    if (this.inventaireActive && endsAdministrativeInventory(cleaned)) this.inventaireActive = false;

    // 1) Tag [Hector-XX] pleine-ligne (regex ouverte → forward-compatible Custom7, etc.)
    //
    // CRITIQUE : ce handler doit appliquer les MÊMES state transitions que
    // les handlers headings markdown (§2) et marker rules (§4). Sans ça, le
    // LLM qui émet en tags `[Hector-H1]I. RAPPEL[/Hector-H1]` ou
    // `[Hector-Pieces-Jointes]...[/Hector-Pieces-Jointes]` court-circuite
    // BordereauStateManager + page break section romaine + détection MEnD.
    // Symptômes observés user 2026-06-09 : pas de page break sur "I.",
    // pièces post-bordereau restent en Piece-Before (bold right au lieu de
    // normal left).
    const hector = HECTOR_PARAGRAPH.exec(line);
    if (hector && hector[1] !== undefined && hector[2] !== undefined) {
      let styleKey = `Hector-${hector[1]}` as HectorStyleKey;
      const content = hector[2].trim();
      let pageBreakBefore: boolean | undefined;

      // ─── Headings via tag : applique la logique heading markdown ──────────
      // Détection type document (mise en demeure / renvoi / etc.) sur H1.
      if (styleKey === 'Hector-H1' && isMiseEnDemeureDocTitle(content)) {
        this.isMiseEnDemeureDoc = true;
      }
      // Section romaine `I.` via heading tag → page break si premier.
      if (
        (styleKey === 'Hector-H1' ||
          styleKey === 'Hector-H2' ||
          styleKey === 'Hector-H3') &&
        startsWithRomanI(content)
      ) {
        if (!this.seenRomanSection && !this.seenPLAISE && !this.isMiseEnDemeureDoc) {
          pageBreakBefore = true;
        }
        this.seenRomanSection = true;
        this.reducedSpacingActive = false;
      }

      // ─── BordereauStateManager via tag ─────────────────────────────────────
      // [Hector-BORDEREAU] / [Hector-Pieces-Jointes] activent le mode
      // post-bordereau pour les pièces suivantes.
      if (styleKey === 'Hector-BORDEREAU' || styleKey === 'Hector-Pieces-Jointes') {
        this.bordereauActive = true;
      }
      if (styleKey === 'Hector-BORDEREAU' && isInventaireHeadingLine(content)) {
        this.inventaireActive = true;
      }
      // Pièce post-bordereau → swap Piece-Before → Piece-After.
      if (styleKey === 'Hector-Piece-Before' && this.bordereauActive) {
        styleKey = 'Hector-Piece-After';
      }

      // ─── PLAISE via tag ────────────────────────────────────────────────────
      // 1er [Hector-PLAISE] = pageBreak + style centered ; suivants =
      // PLAISE-Body. Identique à la marker rule.
      if (styleKey === 'Hector-PLAISE') {
        if (this.seenPLAISE) {
          styleKey = 'Hector-PLAISE-Body';
        } else {
          this.seenPLAISE = true;
          pageBreakBefore = true;
        }
      }

      this.emitParagraph({
        kind: 'paragraph',
        styleKey,
        runs: this.parseInlineWithState(content),
        pageBreakBefore,
      });
      return;
    }
    // 2) Heading markdown (#..######, 7e niveau capé H6)
    const h = /^(#{1,7})\s+(.+)$/.exec(line);
    if (h && h[1] !== undefined && h[2] !== undefined) {
      const level = Math.min(h[1].length, 6);
      const headingText = sanitizeHeading(h[2]);

      // ─── Heading qui contient un marqueur sémantique : RE-ROUTE ──────────
      // Le LLM ouvre régulièrement "## BORDEREAU DES PIÈCES", "## À LA
      // REQUÊTE DE :", "## OBJET :", "### Pièce n°3 : ..." en pensant à
      // un heading visuel — mais sémantiquement c'est un marqueur Hector
      // (BORDEREAU activeur de state, Pièce/Marqueur styles dédiés). Sans
      // re-route, ils tombent sur Hector-H2 (bold+underline+justify) au
      // lieu du style marqueur cible (Marqueur-Bold = bold seul gauche,
      // Piece-Before = bold seul droite).
      //
      // Le re-route ré-injecte la ligne SANS le `#` dans le pipeline pour
      // que les marker rules s'appliquent correctement. Bordereau et Pièce
      // sont explicites parce que leurs regex marker n'acceptent pas le
      // préfixe `**`. Pour le reste, on teste contre TOUTES les marker
      // rules — match → re-emit en texte nu.
      //
      // Remonté user 2026-06-10 : "À LA REQUÊTE DE :", "AYANT POUR AVOCAT
      // :", "J'AI DONNÉ ASSIGNATION À :" apparaissaient soulignés alors
      // que la spec dit bold seul.
      // Détection déléguée aux prédicats partagés (#1001) — plus de regex locale
      // qui puisse diverger de celle de l'add-in.
      const isBordereauHeading = estEnTeteBordereauAncre(headingText);
      const isPieceHeading = estPieceAncre(headingText);
      const matchesAnyMarker = MARKER_RULES.some((rule) => rule.test(headingText));
      // « ## Par lettre recommandée … » : marqueur sémantique lui aussi, mais
      // traité hors MARKER_RULES depuis #1001 — il faut donc le re-router
      // explicitement, sinon il tomberait en Hector-H2 (gras + souligné).
      const isEnvoiRecommandeHeading = estEnvoiRecommande(headingText);
      if (isBordereauHeading || isPieceHeading || matchesAnyMarker || isEnvoiRecommandeHeading) {
        this.emitBlock(headingText);
        return;
      }

      const headingStyle = HEADING_STYLE[level];
      if (!headingStyle) return;

      // Détection du type de document (mise en demeure / renvoi / etc.)
      // sur le H1 — supprime le page break avant la 1ère section romaine.
      if (level === 1 && isMiseEnDemeureDocTitle(headingText)) {
        this.isMiseEnDemeureDoc = true;
      }

      // Section romaine via heading (ex: `## I. ARGUMENTS DE FOND`).
      let pageBreakBefore: boolean | undefined;
      if (startsWithRomanI(headingText)) {
        if (!this.seenRomanSection && !this.seenPLAISE && !this.isMiseEnDemeureDoc) {
          pageBreakBefore = true;
        }
        this.seenRomanSection = true;
        this.reducedSpacingActive = false; // §8 add-in : section romaine désactive
      }

      this.emitParagraph({
        kind: 'paragraph',
        styleKey: headingStyle,
        runs: this.parseInlineWithState(headingText),
        pageBreakBefore,
      });
      return;
    }
    // 2bis) Citation markdown `> …` → Hector-Quote.
    //
    // Le style existe dans le gabarit depuis toujours et n'avait aucune règle
    // qui l'émette. Un chevron par ligne = un paragraphe cité : GFM fusionne
    // les lignes contiguës d'un blockquote, mais un acte cite des alinéas
    // distincts (articles, considérants) qu'il ne faut pas coller.
    //
    // Placé APRÈS les délimiteurs structurels malformés (`[/PARA>`), déjà
    // retirés par `stripDelims` en amont — sans quoi leur chevron final
    // ouvrirait une fausse citation.
    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) {
      const cited = (quote[1] ?? '').trim();
      if (!cited) return;
      this.emitParagraph({
        kind: 'paragraph',
        styleKey: 'Hector-Quote',
        runs: this.parseInlineWithState(cited),
      });
      return;
    }
    // 3) Séparateur ---/_3+ (ignoré markdown noise).
    if (/^[-_]{3,}$/.test(line)) return;
    // 3bis) Séparateur typographique `* * *` / `***` / `* * * *` — centré, sans
    // styling spécifique. Émis comme paragraphe Hector-Normal centré (port
    // direct add-in `wordInserter.ts` détection séparateur visuel).
    if (/^(\*\s*){2,5}$/.test(line)) {
      this.emitParagraph({
        kind: 'paragraph',
        styleKey: 'Hector-Normal',
        runs: [{ text: line.trim() }],
        leadingBreaks: 1,
        trailingBreaks: 1,
      });
      return;
    }
    // 3ter) « Par lettre recommandée … » — AVANT la boucle des règles (#1001).
    // Italique + souligné portés par les runs, pas par un style (cf.
    // ENVOI_RECOMMANDE / D6). leading/trailingBreaks=1 : conserve l'aération
    // que la ligne recevait quand elle matchait encore l'alternance
    // Marqueur-Bold, avant que la détection ne soit desserrée.
    if (estEnvoiRecommande(cleaned)) {
      const runs = this.parseInlineWithState(cleaned.replace(/\*\*/g, '')).map((run) => ({
        ...run,
        italic: true,
        underline: true,
      }));
      this.emitParagraph({
        kind: 'paragraph',
        styleKey: 'Hector-Normal',
        runs,
        leadingBreaks: 1,
        trailingBreaks: 1,
      });
      return;
    }

    // 4) Marqueurs juridiques (premier match)
    for (const rule of MARKER_RULES) {
      if (rule.test(cleaned)) {
        // ─── State adjustments selon le marqueur matché ───────────────────
        let styleKey: HectorStyleKey = rule.styleKey;
        let pageBreakBefore = rule.pageBreakBefore;

        // PLAISE : 1er = pageBreak + center bold ; suivants = PLAISE-Body
        // (justified, sans page break — cf. styleSpec).
        if (rule.styleKey === 'Hector-PLAISE') {
          if (this.seenPLAISE) {
            styleKey = 'Hector-PLAISE-Body';
            pageBreakBefore = undefined;
          } else {
            this.seenPLAISE = true;
          }
        }

        // BordereauStateManager (port add-in `stateManagers.ts:74-103`) — dès
        // qu'on rencontre un marqueur BORDEREAU / LISTE DES PIÈCES / PIÈCES
        // JOINTES, on bascule en mode "post-bordereau" : les pièces suivantes
        // (`Pièce n°X : ...`) seront stylées Hector-Piece-After (normal,
        // gauche) au lieu de Hector-Piece-Before (bold, droite). Cohérent
        // avec la convention add-in : les pièces AVANT le bordereau sont
        // citées dans le corps de l'acte (bold, alignées droite), les
        // pièces APRÈS sont énumérées dans le bordereau lui-même (normal).
        if (
          rule.styleKey === 'Hector-BORDEREAU' ||
          rule.styleKey === 'Hector-Pieces-Jointes'
        ) {
          this.bordereauActive = true;
        }
        if (rule.styleKey === 'Hector-BORDEREAU' && isInventaireHeadingLine(cleaned)) {
          this.inventaireActive = true;
        }

        // Swap Piece-Before → Piece-After si on est sous bordereau.
        if (rule.styleKey === 'Hector-Piece-Before' && this.bordereauActive) {
          styleKey = 'Hector-Piece-After';
        }

        // (Les bascules reduced-spacing sont évaluées en tête d'emitBlock —
        // §0 ci-dessus — pour tous les chemins d'émission, plus seulement ici.)

        this.emitParagraph({
          kind: 'paragraph',
          styleKey,
          runs: this.parseInlineWithState(cleaned.replace(/\*\*/g, '')),
          pageBreakBefore,
          leadingBreaks: rule.leadingBreaks,
          trailingBreaks: rule.trailingBreaks,
        });
        return;
      }
    }
    // 4bis) Hyperlien autonome [URL_HYPERLINK:url] — paragraphe dédié bleu
    // souligné cliquable. Port du chemin add-in `wordInserter.ts:1736-1759`.
    const urlMatch = /^\[URL_HYPERLINK:(\S+?)\]$/.exec(cleaned);
    if (urlMatch && urlMatch[1]) {
      const href = urlMatch[1];
      this.emitParagraph({
        kind: 'paragraph',
        styleKey: 'Hector-Normal',
        runs: estUrlWebSure(href)
          ? [{ text: href, href, color: '#0563C1', underline: true }]
          : [{ text: href }],
      });
      return;
    }

    // 4ter) Ligne d'inventaire administratif « 1 Carte d'identité » (#1704) — sous un titre
    // INVENTAIRE DES PIÈCES seulement, elle reçoit le style d'une « Pièce n°1 : … » sous
    // bordereau. APRÈS la boucle des marqueurs : premier match inchangé pour toute ligne
    // qu'une règle reconnaissait déjà. Une puce « - 1 Carte » n'arrive jamais ici : c'est un
    // item de liste, exactement comme « - Pièce n°1 : Carte » aujourd'hui.
    if (this.inventaireActive && isInventairePieceLine(cleaned)) {
      this.emitParagraph({
        kind: 'paragraph',
        styleKey: 'Hector-Piece-After',
        runs: this.parseInlineWithState(cleaned.replace(/\*\*/g, '')),
      });
      return;
    }

    // 5) Section romaine en corps de texte (sans `##`) — fallback pour les
    // documents où le LLM oublie le `##`. Port `wordInserter.ts:1809`.
    let bodyPageBreak: boolean | undefined;
    if (startsWithRomanI(cleaned)) {
      if (!this.seenRomanSection && !this.seenPLAISE && !this.isMiseEnDemeureDoc) {
        bodyPageBreak = true;
      }
      this.seenRomanSection = true;
      this.reducedSpacingActive = false;
    }

    // 6) Paragraphe "plain". Si reducedSpacing actif (suit POUR/CONTRE/etc.),
    // swap vers Hector-Normal-Compact (spaceAfter=0) — port direct add-in
    // `wordInserter.ts:1924-1927`.
    const plainStyleKey: HectorStyleKey = this.reducedSpacingActive
      ? 'Hector-Normal-Compact'
      : 'Hector-Normal';
    this.emitParagraph({
      kind: 'paragraph',
      styleKey: plainStyleKey,
      runs: this.parseInlineWithState(cleaned),
      pageBreakBefore: bodyPageBreak,
    });
  }

  /**
   * Parse inline AVEC état cross-paragraphe — si le bloc précédent a laissé
   * un quote ouvert, ce bloc commence en italique jusqu'au fermant.
   *
   * Port direct du `CrossParagraphQuoteStateManager` add-in §6. Crucial
   * pour les longues citations d'articles que le LLM coupe sur plusieurs
   * paragraphes (« Les contrats légalement formés [/PARA] tiennent lieu
   * de loi... »).
   */
  private parseInlineWithState(text: string): InlineRun[] {
    const state = this.crossQuote;

    // Cas A : on entre dans le bloc en mode quote ouvert.
    if (state.isInside) {
      const expectedClose = QUOTE_CLOSE[state.openChar!] ?? '»';
      const closeIdx = text.indexOf(expectedClose);
      if (closeIdx === -1) {
        // Pas de fermant dans ce bloc → tout italique, état reste ouvert.
        return [{ text, italic: true }];
      }
      // Fermant trouvé → italique jusqu'au fermant inclus, puis on parse le
      // reste normalement avec mise à jour de l'état.
      const italicPart: InlineRun = { text: text.slice(0, closeIdx + 1), italic: true };
      const rest = text.slice(closeIdx + 1);
      state.isInside = false;
      state.openChar = null;
      const restRuns = parseInline(rest);
      this.updateCrossQuoteState(rest);
      return [italicPart, ...restRuns];
    }

    // Cas B : pas de quote ouvert au début. `parseInline` connaît une paire
    // complète « … », mais pas l'ouvrant qui se poursuivra dans le bloc
    // suivant. Découper avant cet ouvrant permet de conserver ses autres
    // marques inline (gras, lien, souligné) tout en ajoutant l'italique dès le
    // premier bloc — la même continuité que le chemin Cas A.
    const unclosedQuoteStart = this.updateCrossQuoteState(text);
    const runs = unclosedQuoteStart === null
      ? parseInline(text)
      : [
          ...parseInline(text.slice(0, unclosedQuoteStart)),
          ...parseInline(text.slice(unclosedQuoteStart)).map((run) => ({ ...run, italic: true })),
        ];
    return runs;
  }

  /**
   * Walk char-by-char, met à jour l'état de citation et retourne l'offset d'un
   * ouvrant de ce bloc qui reste non fermé. Les guillemets symétriques
   * alternent ; les guillemets français attendent leur fermant dédié.
   */
  private updateCrossQuoteState(text: string): number | null {
    const state = this.crossQuote;
    let unclosedQuoteStart: number | null = null;

    for (let index = 0; index < text.length; index += 1) {
      const ch = text[index] ?? '';
      if (state.isInside) {
        const expected = QUOTE_CLOSE[state.openChar!];
        if (ch === expected) {
          state.isInside = false;
          state.openChar = null;
          unclosedQuoteStart = null;
        }
      } else if (ch in QUOTE_CLOSE) {
        state.isInside = true;
        state.openChar = ch;
        unclosedQuoteStart = index;
      }
    }

    return state.isInside ? unclosedQuoteStart : null;
  }
}

function sanitizeHeading(s: string): string {
  return s.replace(/\[\/?(TITRE|SECTION|PARA)[\]>]/gi, '').replace(/\*\*/g, '').trim();
}

/**
 * Une règle de formatage inline : un délimiteur, et les marques qu'il pose.
 *
 * `content` rend le texte INTÉRIEUR, re-parsé récursivement — c'est ce qui
 * permet à `**un *mot* gras**` de sortir avec les deux marques cumulées.
 */
interface InlineRule {
  re: RegExp;
  mark: (m: RegExpExecArray) => Partial<InlineRun>;
  content: (m: RegExpExecArray) => string;
}

/**
 * Règles de formatage inline, **par ordre de priorité**.
 *
 * L'ordre est load-bearing sur deux points :
 *  - `***` avant `**` avant `*`, sinon `***les deux***` sortirait avec une
 *    astérisque orpheline collée au texte ;
 *  - le lien avant tout le reste, pour qu'un libellé de lien puisse porter du
 *    gras sans que le `[` ne soit consommé par autre chose.
 *
 * **Limite assumée du dialecte** : les délimiteurs d'emphase ne peuvent pas
 * contenir le caractère qui les délimite (`[^*]`, `[^_]`, `[^~]`). Les formes
 * de chevauchement partiel de GFM (`**gras et *les deux***`) ne sont donc PAS
 * reconnues et ressortent en texte littéral, sans perte de caractère. Un vrai
 * analyseur d'emphase GFM est un automate à part entière, et le sur-coût n'est
 * pas justifié pour de la rédaction juridique : la forme `***les deux***`, elle,
 * est reconnue. Toute tentative d'élargir ces classes de caractères doit être
 * mesurée contre le cas `2 * 3 = 6`, qui ne doit jamais devenir de l'italique.
 */
const INLINE_RULES: InlineRule[] = [
  // Souligné — SEULE balise HTML de la liste blanche. Elle existe parce que le
  // markdown n'a aucune syntaxe de souligné, et que « souligne l'objet » est
  // une demande courante d'un avocat. Tout autre balisage retombe en texte
  // littéral (le convertisseur HTML échappe `<` en sortie).
  { re: /<u>([\s\S]+?)<\/u>/, mark: () => ({ underline: true }), content: (m) => m[1] ?? '' },
  // Lien markdown. Exige les parenthèses : `[Hector-H1]` et
  // `[URL_HYPERLINK:https://x.fr]` n'en ont pas et restent donc du texte —
  // c'est ce qui empêche ce chemin d'avaler les balises de style et le
  // marqueur d'hyperlien pleine-ligne, qui ont leurs propres traitements.
  {
    re: /\[([^\]]+)\]\((\S+?)\)/,
    mark: (m) => estUrlWebSure(m[2] ?? '')
      ? { href: m[2], color: '#0563C1', underline: true }
      : {},
    content: (m) => m[1] ?? '',
  },
  { re: /\*\*\*([^*]+)\*\*\*/, mark: () => ({ bold: true, italic: true }), content: (m) => m[1] ?? '' },
  { re: /\*\*([^*]+)\*\*/, mark: () => ({ bold: true }), content: (m) => m[1] ?? '' },
  // Italique `*…*` — le contenu ne peut ni commencer ni finir par une espace,
  // ce qui écarte `2 * 3 = 6` et le séparateur typographique `* * *`.
  {
    re: /(?<![\w*])\*([^*\s](?:[^*]*[^*\s])?)\*(?![\w*])/,
    mark: () => ({ italic: true }),
    content: (m) => m[1] ?? '',
  },
  // Italique `_…_` — les gardes `\w` de part et d'autre écartent les
  // identifiants (`snake_case_ici`), exactement comme GFM.
  {
    re: /(?<![\w_])_([^_\s](?:[^_]*[^_\s])?)_(?![\w_])/,
    mark: () => ({ italic: true }),
    content: (m) => m[1] ?? '',
  },
  { re: /~~([^~]+)~~/, mark: () => ({ strikethrough: true }), content: (m) => m[1] ?? '' },
];

/**
 * Applique les règles inline en descente récursive : le match retenu découpe le
 * texte en trois (avant / contenu / après), le contenu est re-parsé avec la
 * marque ajoutée, l'avant et l'après avec la marque courante.
 *
 * ⚠️ **Le match retenu est le plus à GAUCHE**, la priorité de la table ne
 * servant qu'à départager deux règles qui matchent à la même position. Prendre
 * la première règle qui matche où que ce soit était faux : sur
 * `**gras avec <u>souligné</u> dedans**`, la règle du souligné matchait à
 * l'intérieur du gras et découpait le texte au milieu de la paire `**`, qui ne
 * se refermait alors jamais — les astérisques ressortaient littéralement et le
 * gras était perdu. Le délimiteur ouvert le plus à gauche est celui qui englobe,
 * donc c'est lui qui doit découper en premier.
 */
function parseMarks(text: string, base: Partial<InlineRun>, runs: InlineRun[]): void {
  if (!text) return;
  let best: { rule: InlineRule; m: RegExpExecArray } | null = null;
  for (const rule of INLINE_RULES) {
    const m = rule.re.exec(text);
    if (!m) continue;
    // `<` strict : à position égale, la règle la plus prioritaire (donc vue en
    // premier) l'emporte — c'est ce qui fait gagner `***` sur `**`.
    if (best === null || m.index < best.m.index) best = { rule, m };
  }
  if (best === null) {
    // Feuille : plus aucun délimiteur — reste l'italique par guillemets.
    pushQuoted(runs, text, base);
    return;
  }
  const { rule, m } = best;
  parseMarks(text.slice(0, m.index), base, runs);
  parseMarks(rule.content(m), { ...base, ...rule.mark(m) }, runs);
  parseMarks(text.slice(m.index + m[0].length), base, runs);
}

/**
 * Parse le formatage inline : gras, italique, barré, souligné, liens, et
 * guillemets typographiques «…»/"…"/“…” → italique.
 *
 * Exclut les apostrophes françaises ` ' ` (sinon « l'article 12 » deviendrait italique).
 */
export function parseInline(text: string): InlineRun[] {
  const runs: InlineRun[] = [];
  parseMarks(text, {}, runs);
  return runs.length ? runs : [{ text }];
}

/**
 * URL nue dans du texte courant (2026-08-28).
 *
 * L'annexe des jurisprudences d'un acte sort en `Réf. — https://…` : l'URL est
 * du TEXTE, et rien ne la rendait cliquable — il n'existait aucun autolink, seul
 * `[texte](url)` produisait un lien. L'add-in Word, lui, repérait déjà les URL
 * nues et les convertissait ; le canvas était le seul des deux à ne pas le faire.
 *
 * Trois exclusions, chacune payée :
 * - `(?<![(:\w])` — jamais après `(` ni `:` : ce sont les deux seules positions
 *   où une URL est DÉJÀ portée par une syntaxe, `[texte](url)` et
 *   `[URL_HYPERLINK:url]`. Sans elles, on doublerait un lien existant ou on
 *   couperait le marqueur pleine-ligne en deux.
 * - `[...]`, `<>`, guillemets et parenthèses hors de l'URL : sinon
 *   « (voir https://x.fr) » avalerait la parenthèse fermante dans le lien.
 * - Le dernier caractère ne peut être une ponctuation : une URL en fin de phrase
 *   emporterait le point, et le lien pointerait à côté.
 */
const URL_NUE = /(?<![(:\w])(https?:\/\/[^\s<>«»"'[\]()]*[^\s<>«»"'[\]().,;:!?])/g;

function pushQuoted(runs: InlineRun[], text: string, base: Partial<InlineRun>): void {
  // ⚠️ Jamais d'autolink SOUS un lien : `base.href` non nul signifie qu'on parse
  // le libellé d'un `[texte](url)`. Y reposer un lien produirait une ancre
  // imbriquée, dont ni le canal HTML ni l'export Word ne savent quoi faire.
  if (base.href === undefined) {
    // `split` sur un motif à UN groupe capturant intercale les captures : les
    // rangs impairs sont les URL, les pairs le texte qui les entoure.
    const morceaux = text.split(URL_NUE);
    if (morceaux.length > 1) {
      morceaux.forEach((morceau, rang) => {
        if (!morceau) return;
        if (rang % 2 === 1 && estUrlWebSure(morceau)) {
          runs.push({ text: morceau, ...base, href: morceau, color: '#0563C1', underline: true });
          return;
        }
        pushGuillemets(runs, morceau, base);
      });
      return;
    }
  }
  pushGuillemets(runs, text, base);
}

function pushGuillemets(runs: InlineRun[], text: string, base: Partial<InlineRun>): void {
  // Italique sur le contenu entre guillemets typographiques/français.
  const parts = text.split(/(«[^»]*»|“[^”]*”|"[^"]*")/g);
  for (const p of parts) {
    if (!p) continue;
    const isQuote = /^(«.*»|“.*”|".*")$/.test(p);
    // ⚠️ Ne poser `italic` QUE s'il est vrai. La version précédente écrivait
    // `italic: false` sur tous les runs ordinaires : sans conséquence de rendu
    // (les consommateurs testent la véracité), mais un run nu n'était plus
    // égal à `{ text }`, ce qui rend toute assertion de forme illisible et
    // alourdit chaque bloc transporté sur le fil.
    const run: InlineRun = { text: p, ...base };
    if (isQuote) run.italic = true;
    runs.push(run);
  }
}
